import { subject } from '@casl/ability';
import { isPlatform } from '@ionic/core';
import {
  IonButton,
  IonChip,
  IonLabel,
  IonList,
  IonListHeader,
  IonRadio,
  IonText,
  IonToolbar,
} from '@ionic/react';
import { Form, Formik, FormikProps } from 'formik';
import { ReactElement, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Can, useHasPermission } from '../../api/authorization.provider';
import {
  AuthMethod,
  GetUserDocument,
  ListTeamsInput,
  ListTeamsQuery,
  PlanEntitlementKey,
  Team,
  User,
  useDisableUserMutation,
  useEnableUserMutation,
  useGetUserQuery,
  useListTeamsQuery,
} from '../../api/graphql/generated';
import { ImageResolution } from '../../api/hooks/attachment.hooks';
import { useListQueryWithInfiniteScroll } from '../../api/hooks/infinite-scroll.hooks';
import { useCurrentUser, useUpdateUser } from '../../api/user.hooks';
import {
  trackDisabledUser,
  trackEnabledUser,
  trackNavigatedFromUserToTeam,
} from '../../config/analytics';
import { useRedirect } from '../../pages/redirect.hooks';
import { useBeaconRouter } from '../../route/route.hooks';
import { logger } from '../../util/logger';
import { useQueryParameters } from '../../util/router';
import { AvatarEdit } from '../avatar/avatar-edit';
import { RightAlignedButtons } from '../button/aligned-buttons';
import { BaseButton } from '../button/base-button';
import { DeleteButton } from '../button/delete-button';
import { FormSubmitButton } from '../form/form-submit.button';
import { IonFormDateField } from '../form/ion-form-date.field';
import { IonFormSwitchField } from '../form/ion-form-switch.field';
import { IonFormField } from '../form/ion-form.field';
import { IonFormRadioGroupField, RadioOption } from '../form/ion-radio-group';
import { LoadingRetry } from '../graphql/LoadingRetry';
import { IonItem } from '../ionic/IonItem';
import { PaywallIonItem } from '../paywall/paywall-ion-item';
import { getNameForUser } from '../team/team-users';
import { SaveStatusToast } from '../toast/toast.gql';
import { Collapsible } from '../ui/collapsible';
import { ContentWithFooter } from '../ui/content-with-footer';
import { useUserPinCodeConfirmation } from './user-pin-code-confirm';
import { UserPreferenceList } from './user-preferences';
import './user.css';

export interface UserProfileProps {
  id: string;
}

const UserProfile = (props: UserProfileProps): ReactElement | null => {
  const { id } = props;
  const { t } = useTranslation();

  const userSchema = yup.object().shape({
    firstName: yup.string().nullable().required('*'),
    lastName: yup.string().nullable().required('*'),
    email: yup.string().email(t('email')).required('*'),
    admin: yup.boolean(),
  });

  const currentUser = useCurrentUser();

  const params = useQueryParameters();
  const email = params.get('email');

  const redirect = useRedirect();

  const formikRef = useRef<FormikProps<Partial<User>> | null>(null);

  // check to see if we can update a user to have a password auth method.
  // if so, then we know can extend our auth method picklist options
  // to include the password auth method
  const hasPasswordAuthMethodSupport = useHasPermission<Partial<User>>(
    'update',
    'user',
    {
      authMethod: AuthMethod.PASSWORD,
    }
  );

  const [updateUser, { loading, error }] = useUpdateUser();
  const {
    data,
    loading: userLoading,
    error: userError,
    refetch: userRefetch,
  } = useGetUserQuery({
    variables: { input: { id } },
    skip: id === 'new',
    fetchPolicy: 'cache-and-network',
  });

  const [disableUser] = useDisableUserMutation({
    variables: { input: { id } },
    refetchQueries: [
      {
        query: GetUserDocument,
        variables: { input: { id } },
      },
    ],
  });
  const [enableUser] = useEnableUserMutation({
    variables: { input: { id } },
    refetchQueries: [
      {
        query: GetUserDocument,
        variables: { input: { id } },
      },
    ],
  });

  const onSubmit = useCallback(
    async (input: Partial<User> & { pinCode?: string }) => {
      try {
        const { avatarAttachment: _avatarAttachment, ...userPropsToSave } =
          input;

        await updateUser({
          variables: {
            input: userPropsToSave,
          },
        });
        redirect();
        logger.info('Successfully updated user record!');
      } catch (error) {
        logger.error('Error updating user record', { error, input });
      }
    },
    [redirect, updateUser]
  );

  const authMethodOptions = useMemo((): RadioOption[] => {
    const options: RadioOption[] = [];

    options.push({
      label: t('magic_link'),
      value: AuthMethod.MAGIC_LINK,
    });

    options.push({
      label: t('password'),
      ionItem: (
        <PaywallIonItem
          planEntitlementKey={PlanEntitlementKey.USER_PASSWORD_AUTH_METHOD}
          ionItemProps={{
            detail: false,
            routerLink: hasPasswordAuthMethodSupport ? undefined : '#',
          }}
          isFeatureAvailable={() => hasPasswordAuthMethodSupport}
        >
          {hasPasswordAuthMethodSupport ? (
            <IonRadio value={AuthMethod.PASSWORD}>{t('password')}</IonRadio>
          ) : (
            <IonLabel>{t('password')}</IonLabel>
          )}
        </PaywallIonItem>
      ),
    });

    return options;
  }, [hasPasswordAuthMethodSupport, t]);

  const onPinCodeConfirm = useCallback(
    async (pinCode: string) => {
      await onSubmit({
        ...formikRef.current?.values,
        pinCode,
      });
    },
    [onSubmit]
  );

  const { present: presentPinCodeConfirmation } = useUserPinCodeConfirmation({
    onPinCodeConfirm,
  });

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const { __typename, ...user } = data?.getUser ?? ({ email } as Partial<User>);

  const isUsersOwnProfile =
    user.id && user.id === currentUser?.id ? true : false;

  return user.id ? (
    <Formik
      validationSchema={userSchema}
      onSubmit={onSubmit}
      initialValues={user}
      enableReinitialize={true}
      validateOnMount={true}
      innerRef={(instance) => (formikRef.current = instance)}
    >
      {({ isValid, dirty, submitForm }) => {
        const footer = (
          <IonToolbar>
            <RightAlignedButtons>
              {currentUser?.admin &&
                !isUsersOwnProfile &&
                user.active === true && (
                  <DeleteButton
                    header={t('user_deactivate_confirmation_header')}
                    onDelete={async () => {
                      await disableUser();
                      trackDisabledUser();
                    }}
                  >
                    {t('user_deactivate')}
                  </DeleteButton>
                )}
              {currentUser?.admin &&
                !isUsersOwnProfile &&
                user.active !== true && (
                  <BaseButton
                    buttonProps={{
                      fill: 'outline',
                      onClick: async () => {
                        await enableUser();
                        trackEnabledUser();
                      },
                    }}
                  >
                    {t('user_reactivate')}
                  </BaseButton>
                )}
              <Can I="update" this={subject('user', user)}>
                <FormSubmitButton
                  loading={loading}
                  disabled={!isValid || !dirty || user.active !== true}
                  buttonProps={{
                    onClick: submitForm,
                  }}
                />
              </Can>
            </RightAlignedButtons>
          </IonToolbar>
        );

        return (
          <ContentWithFooter footer={footer}>
            <LoadingRetry
              loading={userLoading}
              error={userError}
              refetch={userRefetch}
              results={{
                hasResults: user.email ? true : false,
                noResultsComponent: null,
              }}
            >
              <Collapsible collapsed={user.active !== false || loading}>
                <div className="disabled-user-banner">
                  <IonText>{t('user_is_disabled')}</IonText>
                </div>
              </Collapsible>
              <Form className="user-form">
                <IonList lines="full">
                  <IonItem lines="none">
                    <AvatarEdit
                      user={user}
                      resolution={ImageResolution.STANDARD}
                    />
                  </IonItem>
                  <IonFormField
                    label={t('first_name')}
                    name="firstName"
                    inputProps={{
                      type: 'text',
                      autocomplete: 'given-name',
                      autocorrect: 'on',
                      autocapitalize: 'on',
                      enterkeyhint: 'done',
                    }}
                    blurOnEnter={true}
                  />
                  <IonFormField
                    label={t('last_name')}
                    name="lastName"
                    inputProps={{
                      type: 'text',
                      autocomplete: 'family-name',
                      autocorrect: 'on',
                      autocapitalize: 'on',
                      enterkeyhint: 'done',
                    }}
                    blurOnEnter={true}
                  />
                  <IonFormField
                    label={t('email')}
                    name="email"
                    inputProps={{
                      type: 'email',
                      autocomplete: 'email',
                      readonly: true,
                    }}
                  />
                  <IonFormDateField
                    label={t('last_activity')}
                    name="activityAt"
                    inputProps={{
                      readonly: true,
                    }}
                  />
                  {currentUser?.admin && (
                    <IonFormSwitchField
                      label={t('user_admin')}
                      name="admin"
                      inputProps={{ disabled: isUsersOwnProfile }}
                    />
                  )}
                  {currentUser?.admin && (
                    <IonFormSwitchField
                      label={t('user_billing_manager')}
                      name="billingManager"
                    />
                  )}
                  <IonItem>
                    <IonLabel>{t('pin_code')}</IonLabel>
                    <IonButton onClick={presentPinCodeConfirmation}>
                      {t('change')}
                    </IonButton>
                  </IonItem>
                  <IonFormRadioGroupField
                    label={t('authentication_method')}
                    name="authMethod"
                    options={authMethodOptions}
                  />
                </IonList>
              </Form>
              {isUsersOwnProfile && <UserPreferenceList />}
              <TeamMembership user={user} />
              <SaveStatusToast loading={loading} error={error} />
            </LoadingRetry>
          </ContentWithFooter>
        );
      }}
    </Formik>
  ) : null;
};

interface TeamMembershipProps {
  user: Partial<User>;
}

const TeamMembership = (props: TeamMembershipProps): ReactElement => {
  const { user } = props;
  const { t } = useTranslation();

  const {
    infiniteScroll,
    result: { refetch, error, loading, data },
  } = useListQueryWithInfiniteScroll<ListTeamsQuery, ListTeamsInput>({
    useQuery: useListTeamsQuery,
    input: {
      userId: user.id,
    },
    queryOptions: {
      fetchPolicy: 'cache-and-network',
    },
  });

  const teams: Team[] = data?.listTeams.items ?? [];
  const isIOS = isPlatform('ios');

  const { builder } = useBeaconRouter();

  return (
    <LoadingRetry
      loading={loading}
      error={error}
      refetch={refetch}
      results={{
        hasResults: teams.length > 0,
        noResultsComponent: (
          <div className="ion-padding ion-text-center">
            <IonText color="medium">
              {getNameForUser(user)} {t('team_is_not_on_any_teams')}
            </IonText>
          </div>
        ),
      }}
    >
      <IonList lines="full">
        <br />
        <IonListHeader>{t('team_membership')}</IonListHeader>
        {teams.map((team, index) => {
          return (
            <IonItem
              key={index}
              detail={isIOS}
              routerLink={builder.getTeamUsersRoute(team.id)}
              onClick={() => {
                trackNavigatedFromUserToTeam();
              }}
            >
              {team.name}
              {team.role && (
                <IonChip slot="end" color="primary">
                  {team.role}
                </IonChip>
              )}
            </IonItem>
          );
        })}
        {infiniteScroll}
      </IonList>
    </LoadingRetry>
  );
};

export { UserProfile };
